import { ApiError, ticketsApi, MODULE_URLS, getAuthCredentials } from './client'

const BASE = '/api/v1/multichannel'

export interface ChatSessionSummary {
  chat_id: string
  org_id: string
  status: string
  customer_id: string
  customer_name?: string
  customer_email?: string
  agent_id?: string
  ticket_id?: string
  created_at: string
  resolved_at?: string
  last_message?: string
  last_message_at?: string
  last_sender_type?: string
  // Only populated by listSessions (the inbox query) — getSession returns the
  // plain session record without these computed preview fields.
  unread_count?: number
  first_response_due_at?: string
  first_responded_at?: string
  // Computed server-side at query time (not just as-of-last-ticker-scan) —
  // see store.IsSessionBreached in multichannel.
  sla_breached?: boolean
  // Visitor context captured at session creation (widget only — always
  // absent for portal sessions). referrer_url/landing_page_url come from the
  // widget's own document.referrer/location.href, not this request's Referer
  // header, which only reflects the widget's current embed page.
  referrer_url?: string
  landing_page_url?: string
  user_agent?: string
  // Total chat_sessions rows sharing this session's customer_id (the
  // widget's stable per-browser guest ID) — "N past conversations" for a
  // returning visitor. Only populated by listSessions.
  conversation_count?: number
}

// Anonymous widget visitors have no name until a pre-chat "name" step (or an
// agent) sets one — falling back to the raw customer_id (a random per-browser
// guest ID) reads as an opaque blob, so show a short, friendly tag instead.
// Shared by every surface that displays a visitor's identity (the inbox page,
// the floating chat bubble, etc.) so they never drift out of sync with each
// other.
export function friendlyVisitorLabel(customerId: string): string {
  const tail = customerId.replace(/^guest-/, '').slice(-6)
  return `Visitor #${tail}`
}

export interface ChatSLASettings {
  org_id: string
  first_response_minutes: number
  enabled: boolean
}

export interface ChatMessage {
  id: string
  org_id: string
  sender_id: string
  recipient_id: string
  user_type: string
  content: string
  type: string
  attachment_url?: string
  file_name?: string
  chat_id: string
  sent_at: string
  seen: boolean
}

// The system-message keys handler/websocket.go actually reads at runtime
// (sendSystemMsg/sendNoSupportMsg in multichannel) — see
// ORG_SYSTEM_MESSAGE_DEFAULTS below for the fallback text used when an org
// hasn't customized a key yet (mirrors the Go-side defaults in
// internal/store/store.go's GetSystemMessage, so the admin UI's placeholder
// always matches what a visitor would actually see).
export type SystemMessageKey = 'welcome' | 'no_support' | 'agent_unavailable' | 'reassigned'

export const ORG_SYSTEM_MESSAGE_DEFAULTS: Record<SystemMessageKey, string> = {
  welcome:           'Welcome! A support agent will be with you shortly.',
  no_support:        'No support agents are currently available. Please send email to support@multiaccess.io.',
  agent_unavailable: "Your agent is currently unavailable. Your message has been saved and they'll see it as soon as they're back online.",
  reassigned:        "Your previous agent disconnected — we've connected you with another available agent.",
}

export interface SystemMessageRecord {
  message_key: string
  message_text: string
  created_at?: string
  updated_at?: string
}

export type PreChatFieldType = 'choice' | 'name' | 'email' | 'text'

export interface PreChatStep {
  id: number
  org_id: string
  step: number
  question: string
  options: string[] | null
  is_final: boolean
  field_type: PreChatFieldType
}

export interface MyChatSessionResponse {
  chat_id?: string
  resolved: boolean
  messages: ChatMessage[]
}

export const multichannelApi = {
  // Customer-scoped — resolves via the caller's own auth identity, never a
  // client-supplied customer ID. Pass ticketId for Mode 2 (chat with the
  // ticket's assigned agent); omit it for Mode 1 (general support queue).
  // Lets a portal chat popup hydrate its history on open/reconnect instead
  // of starting blank until new live messages arrive.
  myChatSession: (ticketId?: string) =>
    ticketsApi.get<MyChatSessionResponse>(`${BASE}/my-chat${ticketId ? `?ticket_id=${encodeURIComponent(ticketId)}` : ''}`),

  listSessions: (statuses?: string[]) =>
    ticketsApi.get<ChatSessionSummary[]>(
      `${BASE}/support/sessions${statuses?.length ? `?status=${statuses.join(',')}` : ''}`,
    ),
  getSession: (chatId: string) =>
    ticketsApi.get<ChatSessionSummary>(`${BASE}/support/sessions/${chatId}`),
  getMessages: (chatId: string) =>
    ticketsApi.get<ChatMessage[]>(`${BASE}/support/sessions/${chatId}/messages`),
  linkTicket: (chatId: string, ticketId: string) =>
    ticketsApi.patch<{ success: boolean }>(`${BASE}/support/sessions/${chatId}/ticket`, { ticket_id: ticketId }),
  updateCustomerInfo: (chatId: string, info: { name?: string; email?: string }) =>
    ticketsApi.patch<{ success: boolean }>(`${BASE}/support/sessions/${chatId}/customer`, info),
  resolveSession: (chatId: string) =>
    ticketsApi.patch<{ success: boolean }>(`${BASE}/support/sessions/${chatId}/resolve`, {}),

  aiTicketDraft: (chatId: string) =>
    ticketsApi.post<{ title: string; description: string; priority: string; category: string }>(
      `${BASE}/support/sessions/${chatId}/ai-ticket-draft`,
      {},
    ),

  // System messages — admin-configurable text multichannel's WS handler sends
  // during a live chat (NOT the same as ChatWidgetSettings' "Greeting
  // message", which is a static bubble the widget shows client-side before
  // ever connecting; these are sent over the socket mid-session).
  listSystemMessages: () =>
    ticketsApi.get<SystemMessageRecord[]>(`${BASE}/system-messages`),
  // The backend has no upsert — UPDATE silently no-ops if the key was never
  // created, so callers must know whether it already exists. saveSystemMessage
  // takes that as a param instead of re-listing on every save.
  saveSystemMessage: (key: SystemMessageKey, text: string, exists: boolean) =>
    exists
      ? ticketsApi.put<{ status: string }>(`${BASE}/system-messages/${key}`, { text })
      : ticketsApi.post<{ status: string }>(`${BASE}/system-messages`, { key, text }),

  // First-response SLA — alerts agents (real-time over the WS, as a
  // "sla_breach" message) when nobody has replied to a chat within this
  // many minutes. Org-wide, first-response only — see plan doc for why this
  // doesn't mirror ticket-mgmt's per-priority/per-working-group SLA system.
  getChatSlaSettings: () =>
    ticketsApi.get<ChatSLASettings | null>(`${BASE}/chat-sla-settings`).then(settings => ({
      org_id: settings?.org_id ?? '',
      first_response_minutes: settings?.first_response_minutes ?? 5,
      enabled: settings?.enabled ?? true,
    })).catch(err => {
      if (err instanceof ApiError && err.status === 404) {
        return { org_id: '', first_response_minutes: 5, enabled: true }
      }
      throw err
    }),
  saveChatSlaSettings: (firstResponseMinutes: number, enabled: boolean) =>
    ticketsApi.put<{ status: string }>(`${BASE}/chat-sla-settings`, { first_response_minutes: firstResponseMinutes, enabled }),

  // Pre-chat questions — sequential Q&A multichannel asks before routing a
  // brand-new general-support conversation to an agent.
  listPreChatSteps: () =>
    ticketsApi.get<PreChatStep[] | null>(`${BASE}/prechat/`).then(steps => Array.isArray(steps) ? steps : []).catch(err => {
      if (err instanceof ApiError && err.status === 404) return []
      throw err
    }),
  createPreChatStep: (step: number, question: string, options: string[], isFinal: boolean, fieldType: PreChatFieldType) =>
    ticketsApi.post<PreChatStep>(`${BASE}/prechat/`, { step, question, options, is_final: isFinal, field_type: fieldType }),
  updatePreChatStep: (step: number, question: string, options: string[], isFinal: boolean, fieldType: PreChatFieldType) =>
    ticketsApi.put<PreChatStep>(`${BASE}/prechat/${step}`, { question, options, is_final: isFinal, field_type: fieldType }),
  deletePreChatStep: (step: number) =>
    ticketsApi.delete<{ status: string }>(`${BASE}/prechat/${step}`),
  reorderPreChatSteps: (order: number[]) =>
    ticketsApi.post<{ status: string }>(`${BASE}/prechat/reorder`, { order }),
}

// Opens the agent-side WebSocket used to receive/send live messages for the
// signed-in support user. Mirrors the wire protocol in multichannel's
// handler/websocket.go: connect with userType=support, X-Org-ID + token as query
// params (not headers — the gorilla/websocket upgrade happens before any header
// based auth middleware would apply to a browser WebSocket handshake).
export function connectSupportSocket(): WebSocket | null {
  const { token, orgId } = getAuthCredentials()
  if (!token || !orgId) return null

  const apiUrl = MODULE_URLS.tickets || window.location.origin
  const url = new URL(apiUrl)
  const proto = url.protocol === 'https:' ? 'wss:' : 'ws:'
  const wsUrl = `${proto}//${url.host}${BASE}/ws?X-Org-ID=${encodeURIComponent(orgId)}&userType=support&token=${encodeURIComponent(`Bearer ${token}`)}`

  return new WebSocket(wsUrl)
}
